import { z } from 'zod';
import { actionTargetSchema, actionTargetKindSchema } from '../conversations/quick-actions/action-target.js';

// ============================================================================
// Cross-boundary caps
// ============================================================================
/**
 * Maximum number of previously-viewed VDP inventory-item ids the client may
 * echo back on `POST /sales_agent/nudges/resolve`. Consumed by BOTH the widget
 * accumulator (which caps the `sessionStorage` list at this size) and the
 * server zod schema (which rejects a longer array). The two ends live in
 * different packages and would drift silently: bumping one past the other
 * turns every resolve into a 400 the moment the accumulator fills. Exporting
 * one constant is what pins the two in lockstep.
 *
 * Five is the product cap on the `pastVdps` fanning universe. A higher client
 * cap would ship ids the server can never fan, and a lower one would starve
 * the server's ownership/availability filter of candidates.
 */
const VIEWED_VDP_IDS_MAX = 5;
// ============================================================================
// Page bucket
// ============================================================================
/**
 * Page buckets a nudge can target. `general` covers every page that is not a
 * vehicle detail page; `vdp` is set only when the resolver's URL lookup hits
 * an inventory item. A nudge may target both—session state retires it
 * globally on first show.
 */
const nudgePageSchema = z.enum(['general', 'vdp']);
// ============================================================================
// Ordering strategy (how this page view's candidates were ordered)
// ============================================================================
/**
 * Strategy the resolver used to order the eligible nudges before taking the
 * first one. `priority` is the authored `priority` order, `randomized` is a
 * stable per-buyer shuffle, and `ordered` follows an explicit sequence.
 * `suppressed` shows no nudge at all, which serves both as an arm that
 * measures whether nudges help versus showing nothing, and as a dashboard-only
 * kill switch for the whole surface.
 *
 * These are implementation vocabulary, deliberately NOT feature-flag variant
 * names. PostHog requires a variant literally named `control` and reports every
 * experiment result against it, so a variant name denotes an arm's role in
 * whatever experiment is running while a strategy denotes what the resolver
 * does. Binding the two together would mean the control arm of a
 * sequence-versus-sequence experiment silently served the authored order rather
 * than the sequence under test.
 *
 * The applied strategy travels back on the resolve response and is stamped onto
 * the nudge analytics events, so it records what the resolver *did*. PostHog
 * separately attaches the flag's own variant to events; the two disagreeing is
 * the signal that the server fell back and the arm is not what the dashboard
 * believes.
 */
const nudgeOrderingStrategySchema = z.enum([
    'priority',
    'randomized',
    'ordered',
    'suppressed',
]);
/**
 * Upper bound on the anonymous-id string the resolve request carries.
 *
 * The widget sends its durable `ekho_aid` cookie so the server evaluates the
 * ordering flag against the same PostHog `distinct_id` the browser reports,
 * which is what makes server-side bucketing agree with the client's analytics.
 * The endpoint is unauthenticated and CORS-open, so the value is length-bounded
 * on arrival; it is only ever used as a bucketing key and a hash input, and is
 * never logged.
 */
const EKHO_AID_MAX_LENGTH = 128;
/**
 * True when `ekhoAid` is a non-empty string within `EKHO_AID_MAX_LENGTH`.
 * Shared by capture soft-parse, the widget submit path, and socket prior-
 * capture attach so oversized cookie values never become a Firestore key.
 */
function isValidEkhoAid(ekhoAid) {
    return typeof ekhoAid === 'string' && ekhoAid.length > 0 && ekhoAid.length <= EKHO_AID_MAX_LENGTH;
}
// ============================================================================
// Stored asset (what to render)
// ============================================================================
const nudgeIconAssetStoredSchema = z.object({
    kind: z.literal('icon'),
    /** Lookup key into the widget's name→Lucide-element whitelist. */
    name: z.string().min(1),
});
const nudgeImageAssetStoredSchema = z.object({
    kind: z.literal('image'),
    url: z.string().url(),
});
const nudgeCurrentVdpAssetStoredSchema = z.object({
    kind: z.literal('currentVdp'),
});
const nudgePastVdpsAssetStoredSchema = z.object({
    kind: z.literal('pastVdps'),
});
const nudgeAssetSchema = z.discriminatedUnion('kind', [
    nudgeIconAssetStoredSchema,
    nudgeImageAssetStoredSchema,
    nudgeCurrentVdpAssetStoredSchema,
    nudgePastVdpsAssetStoredSchema,
]);
/** Closed set of asset kinds; stamped onto analytics events on the resolved payload. */
const nudgeAssetKindSchema = z.enum(['icon', 'image', 'currentVdp', 'pastVdps']);
// ============================================================================
// Stored action (what happens on click)
// ============================================================================
/**
 * A nudge's stored action is an `ActionTarget`—the same payload shape widget
 * action links carry, since both surfaces dispatch through the same client
 * path. Nudge-specific behavior: the `prompt` member's `text` may carry a
 * `{vehicles}` placeholder that the nudge resolver substitutes server-side
 * before the payload reaches the client. Action links do not have that
 * substitution, which is why the placeholder is documented at this alias
 * site rather than on the shared union.
 */
const nudgeActionSchema = actionTargetSchema;
/**
 * Closed set of action kinds; stamped onto analytics events on the resolved
 * payload. Aliased to `actionTargetKindSchema`—the wire kind is the same
 * closed set for both nudges and action links, and a redeclaration here
 * would silently drift the analytics enum the first time a third kind is
 * added. Mirrors the `nudgeActionSchema = actionTargetSchema` alias one
 * level up.
 */
const nudgeActionKindSchema = actionTargetKindSchema;
// ============================================================================
// Stored nudge (the Firestore document)
// ============================================================================
const nudgeSchema = z.object({
    /** Stable id shared across sessions; the impression/dismiss/click event key. */
    id: z.string().min(1),
    enabled: z.boolean(),
    /** Ascending resolution order; the resolver picks the first eligible nudge by lowest priority. */
    priority: z.number().int(),
    pages: z.array(nudgePageSchema).min(1),
    title: z.string().min(1),
    subtitle: z.string().min(1),
    asset: nudgeAssetSchema,
    action: nudgeActionSchema,
});
// ============================================================================
// Resolved asset (what was actually resolved for this page view)
// ============================================================================
const nudgeIconAssetResolvedSchema = nudgeIconAssetStoredSchema;
const nudgeImageAssetResolvedSchema = nudgeImageAssetStoredSchema;
/**
 * Cap on how many image URLs the resolver may fan into `pastVdps`. Anything
 * beyond three is a broken three-up in the fanned thumbnail component—the
 * cap belongs on the wire contract so the widget cannot receive an
 * un-renderable payload and the server cannot silently ship one.
 */
const PAST_VDPS_MAX = 3;
const nudgeCurrentVdpAssetResolvedSchema = z.object({
    kind: z.literal('currentVdp'),
    /**
     * First image URL for the vehicle on the current VDP. The stored asset
     * declares intent; the resolved payload is the actual URL the widget
     * renders. A missing image makes the nudge ineligible upstream, so this
     * field is never absent on a resolved payload.
     */
    imageUrl: z.string().url(),
});
const nudgePastVdpsAssetResolvedSchema = z.object({
    kind: z.literal('pastVdps'),
    /**
     * Image URLs for previously-viewed vehicles, most-recent-first. Non-empty
     * and capped at three: fewer than one means the nudge is ineligible
     * upstream and would never reach the wire, more than three is a broken
     * three-up in the fanned thumbnail component. Making both bounds part of
     * the contract keeps illegal states unrepresentable at the type level.
     */
    imageUrls: z.array(z.string().url()).min(1).max(PAST_VDPS_MAX),
});
const resolvedNudgeAssetSchema = z.discriminatedUnion('kind', [
    nudgeIconAssetResolvedSchema,
    nudgeImageAssetResolvedSchema,
    nudgeCurrentVdpAssetResolvedSchema,
    nudgePastVdpsAssetResolvedSchema,
]);
// ============================================================================
// Resolved action (what happens on click, already substituted)
// ============================================================================
/**
 * Wire shape after the resolver has substituted every `{vehicles}` placeholder,
 * so the `prompt.text` reaching the client is the literal message the widget
 * sends. The schema is structurally identical to `actionTargetSchema`; the
 * difference is a resolution invariant on `text`, not a shape change.
 */
const resolvedNudgeActionSchema = actionTargetSchema;
// ============================================================================
// Resolved nudge (the wire payload)
// ============================================================================
/**
 * Wire shape returned by the resolver: exactly one nudge, fully resolved for
 * this page view. Analytics reads its `assetKind` and `actionKind` dimensions
 * straight off `asset.kind` and `action.kind`; mirroring them as sibling fields
 * would be two sources for one fact with nothing holding them in agreement.
 * `nudgeAssetKindSchema` and `nudgeActionKindSchema` remain the enums those
 * event properties are typed against.
 */
const resolvedNudgeSchema = z.object({
    id: z.string().min(1),
    title: z.string().min(1),
    subtitle: z.string().min(1),
    asset: resolvedNudgeAssetSchema,
    action: resolvedNudgeActionSchema,
});
// ============================================================================
// Resolve response (POST /sales_agent/nudges/resolve success body)
// ============================================================================
/**
 * Eligible-candidate count on a resolve response. Non-negative integers only:
 * the resolver returns a nudge only when at least one candidate was eligible,
 * and analytics stamps this number as-is.
 */
const nudgeEligibleCountSchema = z.number().int().nonnegative();
/**
 * Success body of `POST /sales_agent/nudges/resolve`.
 *
 * `pageType`, `orderingStrategy`, and `eligibleCount` are present on every
 * success, including the no-nudge and unknown-services-provider bodies, so a
 * client can read them without a second lookup. `nudge` is absent when nothing
 * was selected. `currentVdpInventoryItemId` is present whenever the URL lookup
 * hit an inventory item, whether or not a nudge rendered.
 */
const resolveNudgeResponseSchema = z.object({
    pageType: nudgePageSchema,
    currentVdpInventoryItemId: z.string().min(1).optional(),
    orderingStrategy: nudgeOrderingStrategySchema,
    eligibleCount: nudgeEligibleCountSchema,
    nudge: resolvedNudgeSchema.optional(),
});

export { EKHO_AID_MAX_LENGTH, VIEWED_VDP_IDS_MAX, isValidEkhoAid, nudgeActionKindSchema, nudgeActionSchema, nudgeAssetKindSchema, nudgeAssetSchema, nudgeEligibleCountSchema, nudgeOrderingStrategySchema, nudgePageSchema, nudgeSchema, resolveNudgeResponseSchema, resolvedNudgeActionSchema, resolvedNudgeAssetSchema, resolvedNudgeSchema };
//# sourceMappingURL=Nudge.js.map
